import React, { createContext, useEffect, useMemo, useState } from "react";

export const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCartItems = window.localStorage.getItem("storefront-cart");
      return savedCartItems ? JSON.parse(savedCartItems) : [];
    } catch {
      return [];
    }
  });

  const [wishlist, setWishlist] = useState(() => {
    try {
      const savedWishlist = window.localStorage.getItem("storefront-wishlist");
      return savedWishlist ? JSON.parse(savedWishlist) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem("storefront-cart", JSON.stringify(cartItems));
    } catch {
      // Storage can be unavailable in private or restricted browser contexts.
    }
  }, [cartItems]);

  useEffect(() => {
    try {
      window.localStorage.setItem("storefront-wishlist", JSON.stringify(wishlist));
    } catch {
      // The in-memory state remains fully functional when persistence is blocked.
    }
  }, [wishlist]);

  const addToCart = (productId, quantity = 1, size = "", color = "") => {
    setCartItems((prevItems) => {
      const existingItem = prevItems.find(
        (item) =>
          item.id === productId && item.size === size && item.color === color
      );

      if (existingItem) {
        return prevItems.map((item) =>
          item.id === productId && item.size === size && item.color === color
            ? { ...item, quantity: Math.min(item.quantity + quantity, 10) }
            : item
        );
      } else {
        const newItem = { id: productId, size, color, quantity };
        return [...prevItems, newItem];
      }
    });
  };

  const removeFromCart = (productId, size, color) => {
    setCartItems((prevItems) =>
      prevItems.filter(
        (item) =>
          !(item.id === productId && item.size === size && item.color === color)
      )
    );
  };

  const updateQuantity = (productId, size, color, quantity) => {
    setCartItems((prevItems) =>
      prevItems.map((item) =>
        item.id === productId && item.size === size && item.color === color
          ? { ...item, quantity: Math.min(10, Math.max(1, quantity)) }
          : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
  };

  const addToWishlist = (productId) => {
    setWishlist((current) =>
      current.includes(productId) ? current : [...current, productId]
    );
  };

  const removeFromWishlist = (productId) => {
    setWishlist((current) => current.filter((id) => id !== productId));
  };

  const toggleWishlist = (productId) => {
    setWishlist((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId]
    );
  };

  const cartCount = useMemo(
    () => cartItems.reduce((total, item) => total + item.quantity, 0),
    [cartItems]
  );

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        clearCart,
        updateQuantity,
        cartCount,
        wishlist,
        addToWishlist,
        removeFromWishlist,
        toggleWishlist,
        addCart: addToCart,
        removeCart: removeFromCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
